// Reel data for the slot machine.
// Reel 1 = model labels (image, colored logo).
// Reel 2 = "wildness" parameter (icon + label), controls how unhinged the idea is.
// Reel 3 = "temperature" parameter (icon + label), controls LLM creativity (literally).

export { MODEL_LABELS } from "./models";

/* ── Reel 2: Wildness ────────────────────────────────────────────────────── */

export type WildnessLevel = "safe" | "solid" | "bold" | "wild" | "unhinged";

export interface WildnessOption {
  id: WildnessLevel;
  icon: string; // emoji used on the reel
  label: string;
  /** Short hint injected into the system prompt to flavour the idea. */
  promptHint: string;
}

export const WILDNESS_POOL: WildnessOption[] = [
  {
    id: "safe",
    icon: "🛡️",
    label: "SAFE",
    promptHint:
      "Pick a conservative, proven SaaS pattern. Existing market, known buyer, clear pricing. No moonshots, the kind of idea a YC partner would call 'reasonable'.",
  },
  {
    id: "solid",
    icon: "🎯",
    label: "SOLID",
    promptHint:
      "A concrete, practical product with a sharp wedge. Slightly underserved niche, well-understood buyer, but a non-obvious angle that gives it leverage.",
  },
  {
    id: "bold",
    icon: "⚡",
    label: "BOLD",
    promptHint:
      "A bold, contrarian bet. Either a counter-intuitive customer, a non-obvious distribution channel, or a pricing model people would think is crazy at first.",
  },
  {
    id: "wild",
    icon: "🔥",
    label: "WILD",
    promptHint:
      "Go off-script. Pick a weird niche × weird buyer × weird mechanic. Something that would make a founder laugh first and think 'wait, that might actually work' second.",
  },
  {
    id: "unhinged",
    icon: "🌀",
    label: "UNHINGED",
    promptHint:
      "Be deliberately strange. Combine an unexpected industry with an unexpected wedge with an unexpected go-to-market. The idea should feel uncomfortable. Embrace the chaos but keep at least one anchor in reality.",
  },
];

export const WILDNESS_LABELS = WILDNESS_POOL.map((w) => `${w.icon} ${w.label}`);

export function wildnessByLabel(label: string): WildnessOption | undefined {
  return WILDNESS_POOL.find((w) => `${w.icon} ${w.label}` === label);
}

/* ── Reel 3: Temperature ─────────────────────────────────────────────────── */

export type TemperatureLevel = "cool" | "warm" | "hot" | "molten";

export interface TemperatureOption {
  id: TemperatureLevel;
  icon: string;
  label: string;
  /** Real OpenRouter temperature value applied to the call. */
  value: number;
  /** Hint added to the prompt so the model's "voice" reflects the temperature. */
  promptHint: string;
}

export const TEMPERATURE_POOL: TemperatureOption[] = [
  {
    id: "cool",
    icon: "❄️",
    label: "COOL",
    value: 0.55,
    promptHint:
      "Tone: measured, sober, almost spreadsheet-like. Reach for the most defensible variation of the idea.",
  },
  {
    id: "warm",
    icon: "☀️",
    label: "WARM",
    value: 0.85,
    promptHint:
      "Tone: friendly and concrete, like a senior PM brainstorming. Trust your gut on a non-obvious detail.",
  },
  {
    id: "hot",
    icon: "🔥",
    label: "HOT",
    value: 1.1,
    promptHint:
      "Tone: high-energy, lateral, willing to chase the spicy angle. Don't water it down.",
  },
  {
    id: "molten",
    icon: "🌋",
    label: "MOLTEN",
    value: 1.35,
    promptHint:
      "Tone: feverish. Be playful with naming, lean into surprising verbs, take the weirder option whenever you have a choice.",
  },
];

export const TEMPERATURE_LABELS = TEMPERATURE_POOL.map((t) => `${t.icon} ${t.label}`);

export function temperatureByLabel(label: string): TemperatureOption | undefined {
  return TEMPERATURE_POOL.find((t) => `${t.icon} ${t.label}` === label);
}

/* ── Legacy pools kept for SavedTab filter rendering (decorative only) ───── */
export const NICHE_POOL = [
  "FinTech",
  "EdTech",
  "HealthTech",
  "LegalTech",
  "HR",
  "DevTools",
  "Marketplaces",
  "Real Estate",
  "Logistics",
  "Climate",
  "Gaming",
  "Creator",
  "B2B Sales",
  "Vertical AI",
  "E-commerce",
];

export const CONTEXT_POOL = [
  "North America",
  "LATAM",
  "Europe",
  "Southeast Asia",
  "MENA",
  "Africa",
  "Solopreneurs",
  "Gen Z",
  "SMBs",
  "Enterprise",
  "Indie hackers",
  "YC companies",
  "Agencies",
  "Boutique brands",
];
